import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.png';
import menu from '../../assets/menu-icon.png';
import closeMenu from '../../assets/close-menu-icon.png';
import arrowDown from '../../assets/arrow-down-icon.png';
import './Navbar.css';

const PROGRAM_CATEGORIES = [
  {
    id: 'development',
    title: 'Development Programs',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="2.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="3"  cy="4"  r="1.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="17" cy="4"  r="1.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="3"  cy="16" r="1.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="17" cy="16" r="1.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <line x1="4.5"  y1="4.9"  x2="8"  y2="8.2"  stroke="#9ADBD6" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="15.5" y1="4.9"  x2="12" y2="8.2"  stroke="#9ADBD6" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="4.5"  y1="15.1" x2="8"  y2="11.8" stroke="#9ADBD6" strokeWidth="1.3" strokeLinecap="round"/>
        <line x1="15.5" y1="15.1" x2="12" y2="11.8" stroke="#9ADBD6" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    links: [
      { label: 'Hubs',                to: '/hubs' },
      { label: 'Commercial Programs', to: '/commercial-programs' },
    ],
  },
  {
    id: 'academic',
    title: 'Academic Programs',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3L2 7.5L10 12L18 7.5L10 3Z" stroke="#9ADBD6" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M6 9.5V14C7.5 16 12.5 16 14 14V9.5" stroke="#9ADBD6" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="18" y1="7.5" x2="18" y2="12" stroke="#9ADBD6" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="18" cy="13" r="1" fill="#9ADBD6"/>
      </svg>
    ),
    links: [
      { label: 'Bootcamps',  to: '/bootcamps' },
      { label: 'Hackathons', to: '/hackathons' },
      { label: 'Academy',  to: '/academy' },
    ],
  },
  {
    id: 'engagement',
    title: 'Engagement Programs',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="5"    r="2.5" stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="4"  cy="14.5" r="2"   stroke="#9ADBD6" strokeWidth="1.5"/>
        <circle cx="16" cy="14.5" r="2"   stroke="#9ADBD6" strokeWidth="1.5"/>
        <path d="M7.5 7.5C6.5 9 6 11 6 12.5"   stroke="#9ADBD6" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12.5 7.5C13.5 9 14 11 14 12.5" stroke="#9ADBD6" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="6" y1="14.5" x2="14" y2="14.5" stroke="#9ADBD6" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    links: [
      { label: 'Campus Ambassadors', to: '/ambassadors' },
      { label: 'Women in Quantum',   to: '/wiq' },
      { label: 'Campaigns',          to: '/campaigns' },
    ],
  },
];

const Navbar = () => {
  const [sticky, setSticky] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', () => setSticky(window.scrollY > 50));
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => {
    setMobileMenu(prev => !prev);
    setDropdownOpen(false);
  };

  const handleDropdownClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      setDropdownOpen(prev => !prev);
    }
  };

  const handleDropdownEnter = () => {
    if (!isMobile) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setDropdownOpen(true);
    }
  };

  const handleDropdownLeave = () => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => setDropdownOpen(false), 300);
    }
  };

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('nav')) {
        setMobileMenu(false);
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <nav className={`nav-container ${sticky ? 'dark-nav' : ''}`}>
      <Link to='/' className="nav-logo">
        <img src={logo} alt="logo" className="logo" />
      </Link>

      <ul className={`nav-items ${mobileMenu ? 'show-mobile-menu' : ''}`}>
        <li><Link to='/' onClick={closeAll}>Home</Link></li>
        <li><Link to='/about' onClick={closeAll}>About</Link></li>

        <li
          className={`dropdown ${dropdownOpen ? 'dropdown-active' : ''}`}
          onMouseEnter={handleDropdownEnter}
          onMouseLeave={handleDropdownLeave}
          onClick={handleDropdownClick}
        >
          <span className="dropdown-trigger">
            Programs
            <img src={arrowDown} alt="" className={`arrow-down-icon ${dropdownOpen ? 'rotate' : ''}`} />
          </span>

          <div
            className={`mega-menu ${dropdownOpen ? 'show' : ''}`}
            onMouseEnter={handleDropdownEnter}
          >
            {PROGRAM_CATEGORIES.map((category, index) => (
              <React.Fragment key={category.id}>
                {index > 0 && <div className="mega-menu-divider" />}
                <div className="mega-menu-column">
                  <div className="mega-menu-category-header">
                    {category.icon}
                    <span>{category.title}</span>
                  </div>
                  <ul className="mega-menu-links">
                    {category.links.map(link => (
                      <li key={link.to}>
                        <Link to={link.to} onClick={closeAll}>
                          <span className="mega-link-arrow">›</span>
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </React.Fragment>
            ))}
          </div>
        </li>

        <li><Link to='/events' onClick={closeAll}>Events</Link></li>
        <li><Link to='/support-us' onClick={closeAll}>Support Us</Link></li>
      </ul>

      <div className="nav-right">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="join-button-container"
        >
          <Link to='/join-community' className='join-community-nav'>
            Join Community
          </Link>
        </motion.div>
        <button className="menu-toggle" onClick={toggleMenu}>
          <img
            src={mobileMenu ? closeMenu : menu}
            alt="menu"
            className='menu-icon'
          />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
